import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChevronLeft, Send, Sparkles, Heart, X, Languages, MoreVertical, Flag } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

function useCountdown(expiresAt) {
  const target = useMemo(() => (expiresAt ? new Date(expiresAt).getTime() : 0), [expiresAt]);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const diff = Math.max(0, target - now);
  return {
    label: `${String(Math.floor(diff/3600000)).padStart(2,"0")}:${String(Math.floor((diff%3600000)/60000)).padStart(2,"0")}:${String(Math.floor((diff%60000)/1000)).padStart(2,"0")}`,
    done: target && diff === 0,
  };
}

export default function Chat() {
  const { matchId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [match, setMatch] = useState(null);
  const [content, setContent] = useState("");
  const [sending, setSending] = useState(false);
  const [prompts, setPrompts] = useState([]);
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [decisionLoading, setDecisionLoading] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("harassment");
  const [reportNote, setReportNote] = useState("");
  const [reportLoading, setReportLoading] = useState(false);
  const [translations, setTranslations] = useState({}); // { messageId: text }
  const [translatingId, setTranslatingId] = useState(null);
  const listRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/match/${matchId}/messages`);
      setMessages(data.messages);
      setMatch(data.match);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load chat");
      nav("/today");
    }
  };

  useEffect(() => { load(); }, [matchId]);
  useEffect(() => {
    api.get("/prompts").then(({ data }) => setPrompts(data.prompts)).catch(() => {});
  }, []);
  useEffect(() => {
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [matchId]);
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages.length]);

  const { label: timerLabel, done } = useCountdown(match?.expires_at);

  const send = async (text) => {
    const value = (text ?? content).trim();
    if (!value) return;
    setSending(true);
    try {
      const { data } = await api.post(`/match/${matchId}/messages`, { content: value });
      setMessages((m) => [...m, data]);
      setContent("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const decide = async (yes) => {
    setDecisionLoading(true);
    try {
      const { data } = await api.post(`/match/${matchId}/keep-in-touch`, { decision: yes });
      setMatch(data);
      setDecisionOpen(false);
      if (yes) toast.success("Choice saved. If they also say yes, you'll find them in Kept.");
      else toast.success("Choice saved.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save");
    } finally {
      setDecisionLoading(false);
    }
  };

  const submitReport = async () => {
    setReportLoading(true);
    try {
      await api.post("/report", {
        match_id: matchId,
        reason: reportReason,
        note: reportNote,
        also_block: true,
      });
      toast.success("Reported and blocked. You won't be paired again.");
      setReportOpen(false);
      nav("/today");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't submit report");
    } finally {
      setReportLoading(false);
    }
  };

  const translate = async (m) => {
    if (translations[m.id]) {
      setTranslations((t) => { const c = { ...t }; delete c[m.id]; return c; });
      return;
    }
    if (!user?.is_premium) { nav("/premium"); return; }
    setTranslatingId(m.id);
    try {
      const { data } = await api.post("/translate", {
        text: m.content,
        target_language: user.native_language,
      });
      setTranslations((t) => ({ ...t, [m.id]: data.translation }));
    } catch (e) {
      if (e.response?.status === 402) {
        nav("/premium");
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Translation failed");
      }
    } finally {
      setTranslatingId(null);
    }
  };

  const partner = match?.partner;
  const canSend = match && !match.is_expired && !done;

  return (
    <div className="app-shell flex flex-col h-[100dvh]">
      <header className="sticky top-0 glass border-b border-border/60 z-20 px-4 py-3 flex items-center gap-3" data-testid="chat-header">
        <button onClick={() => nav("/today")} data-testid="chat-back" className="p-1 -ml-1 text-muted-foreground hover:text-foreground transition-colors">
          <ChevronLeft strokeWidth={1.5} />
        </button>
        <div className="flex-1 min-w-0">
          <p className="font-serif text-lg leading-tight truncate" data-testid="chat-partner-name">{partner?.name || "…"}</p>
          <p className="text-[11px] tracking-wide uppercase text-muted-foreground tabular-nums">
            {done ? "Ended" : `${timerLabel} left`}
            {match?.match_type === "exchange" && partner && (
              <span className="ml-2 normal-case tracking-normal">· {partner.native_language} ↔ {partner.learning_language}</span>
            )}
          </p>
        </div>
        <Button
          size="sm" variant="ghost"
          className="rounded-full text-xs tracking-wide uppercase"
          onClick={() => setDecisionOpen(true)}
          data-testid="open-keep-in-touch"
        >
          <Heart className="h-4 w-4 mr-1" strokeWidth={1.5} /> Keep?
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="p-2 -mr-1 text-muted-foreground hover:text-foreground transition-colors"
              data-testid="chat-more-menu"
              aria-label="More"
            >
              <MoreVertical className="h-5 w-5" strokeWidth={1.5} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="rounded-2xl">
            <DropdownMenuItem
              className="text-destructive focus:text-destructive gap-2"
              onClick={() => setReportOpen(true)}
              data-testid="menu-report"
            >
              <Flag className="h-4 w-4" strokeWidth={1.5} />
              Report &amp; block
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-6 space-y-4" data-testid="chat-messages">
        {messages.length === 0 && (
          <div className="text-center pt-10 animate-fade-in">
            <p className="text-xs tracking-[0.3em] uppercase text-muted-foreground">Say hi</p>
            <p className="mt-4 font-serif italic text-lg text-foreground/70 max-w-xs mx-auto">
              A conversation is a room. You just opened the door.
            </p>
          </div>
        )}
        {messages.map((m) => {
          const mine = m.sender_id === user?.id;
          const translated = translations[m.id];
          return (
            <div
              key={m.id}
              className={`flex flex-col ${mine ? "items-end" : "items-start"} gap-1 animate-fade-up`}
              data-testid={mine ? "message-mine" : "message-partner"}
            >
              <div
                className={`max-w-[78%] rounded-3xl px-4 py-2.5 text-[15px] leading-relaxed border ${
                  mine
                    ? "bg-primary text-primary-foreground border-primary/50 rounded-br-md"
                    : "bg-card text-foreground border-black/5 rounded-bl-md"
                }`}
              >
                {m.content}
              </div>
              {!mine && (
                <button
                  onClick={() => translate(m)}
                  disabled={translatingId === m.id}
                  className="text-[11px] tracking-wide uppercase text-muted-foreground hover:text-primary transition-colors flex items-center gap-1 px-2"
                  data-testid={`translate-btn-${m.id}`}
                >
                  <Languages className="h-3 w-3" strokeWidth={1.5} />
                  {translatingId === m.id ? "Translating…" : translated ? "Hide translation" : "Translate"}
                </button>
              )}
              {translated && (
                <p className="max-w-[78%] text-sm italic text-muted-foreground pl-2" data-testid={`translation-${m.id}`}>
                  {translated}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {prompts.length > 0 && messages.length < 4 && canSend && (
        <div className="px-4 pb-2">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="h-3.5 w-3.5 text-secondary" strokeWidth={1.5} />
            <p className="text-[11px] tracking-[0.3em] uppercase text-muted-foreground">Icebreakers</p>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
            {prompts.map((p, i) => (
              <button
                key={i}
                onClick={() => send(p)}
                className="shrink-0 max-w-[260px] text-left text-sm px-4 py-2 rounded-full border border-border bg-card hover:bg-muted transition-colors"
                data-testid={`prompt-${i}`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      )}

      <form
        onSubmit={(e) => { e.preventDefault(); send(); }}
        className="sticky bottom-0 glass border-t border-border/60 px-3 py-3 flex items-center gap-2"
      >
        <Input
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder={canSend ? "Write something honest…" : "This conversation has ended"}
          disabled={!canSend}
          className="h-12 rounded-full bg-card border-transparent focus-visible:border-primary focus-visible:ring-0 px-5"
          data-testid="chat-input"
        />
        <Button
          type="submit" size="icon" disabled={!canSend || sending || !content.trim()}
          className="h-12 w-12 rounded-full bg-primary hover:bg-primary/90 shrink-0"
          data-testid="chat-send"
        >
          <Send className="h-5 w-5" strokeWidth={1.5} />
        </Button>
      </form>

      <Dialog open={decisionOpen} onOpenChange={setDecisionOpen}>
        <DialogContent className="rounded-3xl max-w-sm" data-testid="keep-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Keep in touch with {partner?.name}?</DialogTitle>
            <DialogDescription>
              If you both say yes, they'll appear in your Kept list. Your choice is private until then.
            </DialogDescription>
          </DialogHeader>
          {match?.my_decision !== undefined && match?.my_decision !== null && (
            <p className="text-sm text-muted-foreground">
              You already chose: <span className="text-foreground">{match.my_decision ? "Yes" : "Not this time"}</span>. You can change it below.
            </p>
          )}
          <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2">
            <Button variant="outline" className="rounded-full h-12 flex-1" disabled={decisionLoading} onClick={() => decide(false)} data-testid="keep-no">
              <X className="h-4 w-4 mr-1" strokeWidth={1.5} /> Not this time
            </Button>
            <Button className="rounded-full h-12 flex-1 bg-primary hover:bg-primary/90" disabled={decisionLoading} onClick={() => decide(true)} data-testid="keep-yes">
              <Heart className="h-4 w-4 mr-1" strokeWidth={1.5} /> Yes, keep them
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="rounded-3xl max-w-sm" data-testid="report-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Report {partner?.name}?</DialogTitle>
            <DialogDescription>
              This ends the conversation and blocks them. You won't be paired again. Reports are anonymous.
            </DialogDescription>
          </DialogHeader>
          <RadioGroup value={reportReason} onValueChange={setReportReason} className="space-y-2">
            {[
              ["harassment", "Harassment or hostility"],
              ["hate_speech", "Hate speech or slurs"],
              ["sexual_content", "Unwanted sexual content"],
              ["spam", "Spam or scam"],
              ["under_18", "They appear to be under 18"],
              ["other", "Something else"],
            ].map(([val, label]) => (
              <div key={val} className="flex items-center gap-3">
                <RadioGroupItem value={val} id={`reason-${val}`} data-testid={`reason-${val}`} />
                <Label htmlFor={`reason-${val}`} className="cursor-pointer text-sm font-normal">{label}</Label>
              </div>
            ))}
          </RadioGroup>
          <Textarea
            value={reportNote}
            onChange={(e) => setReportNote(e.target.value)}
            placeholder="Anything else we should know? (optional)"
            className="rounded-2xl bg-card min-h-[70px]"
            maxLength={500}
            data-testid="report-note"
          />
          <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2">
            <Button variant="outline" className="rounded-full h-12 flex-1" disabled={reportLoading} onClick={() => setReportOpen(false)} data-testid="report-cancel">
              Cancel
            </Button>
            <Button
              className="rounded-full h-12 flex-1 bg-destructive hover:bg-destructive/90 text-destructive-foreground"
              disabled={reportLoading}
              onClick={submitReport}
              data-testid="report-submit"
            >
              <Flag className="h-4 w-4 mr-1" strokeWidth={1.5} /> Report &amp; block
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
